'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { getTrustCenterConfig } from '@/services/trust-center'
import type { ITrustCenterConfig } from '@/types/trust-center/trustCenterConfig'
import LoadingPage from '@/app/(public)/trust-center/[orgToken]/components/LoadingPage'
import ErrorPage from '@/app/(public)/trust-center/[orgToken]/components/ErrorPage'

const trustCenterContext = createContext<ITrustCenterConfig | null>(null)

export function TrustCenterProvider({
  children,
  organizationId,
}: {
  children: React.ReactNode
  organizationId: string
}) {
  const [config, setConfig] = useState<ITrustCenterConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchConfig = React.useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const configData = await getTrustCenterConfig(organizationId)
      if ('error' in configData) {
        setError(configData.error.message)
        return
      }
      setConfig(configData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch trust center config')
    } finally {
      setIsLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void fetchConfig()
  }, [fetchConfig])

  if (isLoading) {
    return <LoadingPage />
  }

  if (error || !config) {
    return <ErrorPage error={error} onRetry={fetchConfig} />
  }

  return <trustCenterContext.Provider value={config}>{children}</trustCenterContext.Provider>
}

export function useTrustCenterConfig(): ITrustCenterConfig {
  const context = useContext(trustCenterContext)
  if (!context) {
    throw new Error('useTrustCenterConfig must be used within a TrustCenterProvider')
  }
  return context
}
